import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  CheckCircleOutline as AcceptIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import producerService, { Producer } from '../../../services/producerService';
import { countryCodeToName } from '../../../utils/countryUtils';

interface FormData {
  title: string;
  name: string;
  fullName: string;
  shortName: string;
  country: string;
  region: string;
}

interface FormErrors {
  title?: string;
  name?: string;
  fullName?: string;
  shortName?: string;
  country?: string;
  region?: string;
}

const ProducerForm: React.FC = () => {
  const navigate = useNavigate();
  const { producerId } = useParams<{ producerId?: string }>();
  const isEditMode = Boolean(producerId);
  
  const [formData, setFormData] = useState<FormData>({
    title: '',
    name: '',
    fullName: '',
    shortName: '',
    country: '',
    region: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [needsReview, setNeedsReview] = useState<boolean>(false);
  const theme = useTheme();

  const reviewFieldSx = (isEmpty: boolean) =>
    needsReview && isEmpty
      ? { '& .MuiOutlinedInput-root': { backgroundColor: alpha(theme.palette.error.main, 0.07) } }
      : undefined;

  // Load producer data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode && producerId) {
        setLoading(true);
        try {
          const producer = await producerService.getProducer(producerId);
          setFormData({
            title: producer.title || '',
            name: producer.name,
            fullName: producer.fullName,
            shortName: producer.shortName || '',
            country: producer.country,
            region: producer.region || ''
          });
          setNeedsReview(producer.needsReview ?? false);
        } catch (error) {
          console.error('Failed to fetch producer:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [isEditMode, producerId]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear error when field is changed
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  
  // Handle select changes
  const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = event.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is changed
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.country) {
      newErrors.country = 'Country is required';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };

  // Accept (resolve needsReview without full save)
  const handleAccept = async () => {
    if (!producerId) return;
    setAccepting(true);
    try {
      await producerService.updateProducer(producerId, { needsReview: false });
      setNeedsReview(false);
    } catch {
      alert('Failed to accept. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    try {
      const isComplete =
        formData.name.trim() &&
        formData.fullName.trim() &&
        formData.country &&
        formData.country !== 'UNKNOWN';

      const producerData = {
        title: formData.title.trim() || undefined,
        name: formData.name.trim(),
        fullName: formData.fullName.trim(),
        shortName: formData.shortName.trim() || undefined,
        country: formData.country,
        region: formData.region.trim() || undefined,
        ...(needsReview && isComplete ? { needsReview: false } : {}),
      };

      if (isEditMode && producerId) {
        // Update existing producer
        await producerService.updateProducer(producerId, producerData);
      } else {
        // Create new producer
        await producerService.createProducer(producerData);
      }
      
      // Navigate back to producer list
      navigate('/admin/producers');
    } catch (error) {
      console.error('Failed to save producer:', error);
      alert('Failed to save producer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Get sorted country options
  const countryOptions = Object.entries(countryCodeToName)
    .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB))
    .map(([code, name]) => ({ code, name }));

  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Producer' : 'Create Producer'}
        </Typography>

        {needsReview && isEditMode && (() => {
          const issues: string[] = [];
          if (!formData.name.trim()) issues.push('Name is missing');
          if (!formData.fullName.trim()) issues.push('Full name is missing');
          if (!formData.country || formData.country === 'UNKNOWN') issues.push('Country is unknown or missing');
          return (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ mb: 2, backgroundColor: alpha(theme.palette.warning.main, 0.08), color: 'text.primary', '& .MuiAlert-icon': { color: theme.palette.warning.main } }}
              action={
                <Button
                  size="small"
                  color="success"
                  variant="outlined"
                  startIcon={<AcceptIcon />}
                  onClick={handleAccept}
                  disabled={accepting}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {accepting ? 'Accepting…' : 'Accept'}
                </Button>
              }
            >
              <strong>Needs review</strong> — this record was auto-created during import.
              {issues.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {issues.map((issue) => <li key={issue}>{issue}</li>)}
                </Box>
              )}
              {issues.length === 0 && ' All required fields are filled — saving will also mark this as complete.'}
            </Alert>
          );
        })()}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                margin="normal"
                error={Boolean(errors.title)}
                helperText={errors.title || 'Optional title (e.g., "Domaine", "Château", "Estate")'}
              />
              
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                margin="normal"
                error={Boolean(errors.name)}
                helperText={errors.name || 'Producer name (without title)'}
                required
                sx={reviewFieldSx(!formData.name.trim())}
              />

              <TextField
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                margin="normal"
                error={Boolean(errors.fullName)}
                helperText={errors.fullName || 'Complete producer name including title'}
                required
                sx={reviewFieldSx(!formData.fullName.trim())}
              />
            </Box>
            
            <Box>
              <TextField
                fullWidth
                label="Short Name"
                name="shortName"
                value={formData.shortName}
                onChange={handleInputChange}
                margin="normal"
                error={Boolean(errors.shortName)}
                helperText={errors.shortName || 'Optional abbreviated name'}
              />
              
              <FormControl
                fullWidth
                margin="normal"
                error={Boolean(errors.country)}
                required
                sx={reviewFieldSx(!formData.country || formData.country === 'UNKNOWN')}
              >
                <InputLabel id="country-label">Country</InputLabel>
                <Select
                  labelId="country-label"
                  name="country"
                  value={formData.country}
                  onChange={handleSelectChange}
                  label="Country"
                >
                  {countryOptions.map(({ code, name }) => (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.country && (
                  <FormHelperText>{errors.country}</FormHelperText>
                )}
              </FormControl>
              
              <TextField
                fullWidth
                label="Region"
                name="region"
                value={formData.region}
                onChange={handleInputChange}
                margin="normal"
                error={Boolean(errors.region)}
                helperText={errors.region || 'Wine region or appellation'}
              />
            </Box>
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Producer'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/admin/producers')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ProducerForm;